/**
 * INTERIM module / feature name catalog.
 *
 * Captured 2026-05-14 from the live legacy endpoint
 * `GET https://api-dev.akashic.dhira.io/admin/admin_module/role/modules`.
 *
 * The new tenant-admin `GET /roles/modules` returns only module / feature IDs
 * plus the dependency graph — it has NO names or descriptions. Until the
 * backend adds names to that endpoint (tracked on the backend gap list), the
 * Create / Edit role permission tree sources its labels from this catalog,
 * keyed by the IDs that the live `/roles/modules` call returns.
 *
 * The live `/roles/modules` response stays the source of truth for which
 * modules / features exist and for the dependency graph — this catalog is only
 * consulted for display names. A feature not found here falls back to
 * `Feature {id}`.
 *
 * 👉 Delete this file once `/roles/modules` returns module / feature names.
 */

export interface FeatureCatalogEntry {
  name: string
  description: string
}

export interface ModuleCatalogEntry {
  name: string
  description: string
  features: Record<number, FeatureCatalogEntry>
}

export const ROLE_MODULE_CATALOG: Record<number, ModuleCatalogEntry> = {
  1: {
    name: 'Akashic Data Pipeline (ADP)',
    description: 'Akashic Data Pipeline',
    features: {
      1: {
        name: 'Manage ADP Pipeline',
        description:
          'This allows access to create, update, view and delete ADP pipelines',
      },
      2: {
        name: 'Run/Schedule ADP Pipeline',
        description: 'This allows access to run/schedule ADP pipelines',
      },
      3: {
        name: 'View Pipeline',
        description:
          "Enables access to view ADP pipelines. Doesn't permit edit access",
      },
      4: {
        name: 'View Sources',
        description:
          "Enables access to view ADP registered sources. Doesn't permit edit access",
      },
      5: {
        name: 'View Destination',
        description:
          "Enables access to view ADP registered destinations. Doesn't permit edit access.",
      },
    },
  },
  2: {
    name: 'Akashic Data Warehouse (ADW)',
    description: 'Akashic Data Warehouse',
    features: {
      6: {
        name: 'Manage ADW',
        description:
          'This allows access to create, view and delete analytical data warehouse',
      },
      7: {
        name: 'Manage ADW Pipeline',
        description: 'This allows access to view, create and delete ADM Jobs',
      },
      8: {
        name: 'Run/Schedule ADW Pipeline',
        description: 'This allows access to run/schedule ADW jobs',
      },
      9: {
        name: 'View ADW Pipeline',
        description:
          "Enables access to view ADM Jobs. Doesn't permit edit access",
      },
    },
  },
  3: {
    name: 'Akashic Business Intelligence (ABI)',
    description: 'Akashic Business Intelligence',
    features: {
      10: {
        name: 'Manage Dashboard',
        description:
          'This allows access to create, update, view and delete dashboards',
      },
      11: {
        name: 'View Dashboard',
        description:
          "Enables access to view dashboards. Doesn't permit edit access",
      },
    },
  },
  4: {
    name: 'Akashic Insights (AI)',
    description: 'Akashic Insights',
    features: {
      12: {
        name: 'Ask AI',
        description:
          'This allows access to Asks natural language questions on ADW & destinations',
      },
      13: {
        name: 'Manage Dashboard',
        description:
          'This allows access to create, update, view & delete dashboards',
      },
      14: {
        name: 'View Dashboard',
        description:
          'This allows access to view & filter visuals on dashboards',
      },
    },
  },
  5: {
    name: 'Akashic Machine Learning (AML)',
    description: 'Akashic Machine Learning',
    features: {
      15: {
        name: 'Manage models',
        description:
          'This allows access to create, update, view and delete AML models',
      },
      16: {
        name: 'Manage Pipeline',
        description:
          'This allows access to create, update, view and delete ML pipelines',
      },
      17: {
        name: 'Run/Schedule Pipeline',
        description: 'This allows access to run/schedule AML pipelines',
      },
      18: {
        name: 'View Pipeline',
        description:
          "Enables access to view AML pipelines. Doesn't permit edit access",
      },
      19: {
        name: 'View Models',
        description:
          "Enables access to view AML models. Doesn't permit edit access",
      },
    },
  },
  6: {
    name: 'Akashic Master Data (AMD)',
    description:
      'Module for managing master data entities, hierarchies, and manual records.',
    features: {
      20: {
        name: 'View Entity',
        description: 'View-only access to entity definitions',
      },
      21: {
        name: 'Manage Entity',
        description: 'Create/edit entity and versions',
      },
      22: { name: 'Publish Entity', description: 'Finalize entity version' },
      23: {
        name: 'Manage Hierarchies',
        description: 'Create/edit hierarchy drafts',
      },
      24: {
        name: 'View Hierarchies',
        description: 'View existing hierarchies',
      },
      25: {
        name: 'Manage Manual Record Entry',
        description: 'Manually add or edit master data records',
      },
      26: {
        name: 'Approve/Reject Master Data Changes',
        description: 'Approve or reject changes submitted by others',
      },
      27: {
        name: 'View Master Data',
        description: 'Read-only access to final approved data',
      },
      30: { name: 'Manage Duplicates', description: 'Run Dedupe pipeline' },
    },
  },
  7: {
    name: 'Akashic Data Governance (ADG)',
    description: 'Data Governance module',
    features: {
      34: {
        name: 'Search and browse the catalog',
        description: 'catalog',
      },
      35: {
        name: 'View dataset, pipeline, and dashboard metadata',
        description: 'view metadata',
      },
      36: { name: 'View lineage graphs', description: 'view lineage' },
      37: {
        name: 'View profiling and data quality results',
        description: 'view profiling',
      },
      38: { name: 'Edit descriptions', description: 'edit descriptions' },
      39: {
        name: 'Apply tags and glossary terms',
        description: 'Apply tags and glossary terms',
      },
      40: {
        name: 'Manage classifications',
        description: 'Manage classifications',
      },
      41: {
        name: 'Set and update ownership metadata',
        description: 'Set and update ownership metadata',
      },
      42: {
        name: 'Improve metadata quality post-ingestion',
        description: 'Improve metadata quality post-ingestion',
      },
    },
  },
  8: {
    name: 'Akashic Unstructured Data (AUD)',
    description: 'Process Unstructured Data',
    // No features exposed yet — `/roles/modules` returns a single
    // `{ feature_id: null }` placeholder for this module.
    features: {},
  },
}

/** Display name for a module id, with a safe fallback. */
export function getModuleName(moduleId: number): string {
  return ROLE_MODULE_CATALOG[moduleId]?.name ?? `Module ${moduleId}`
}

/** Display name for a feature id, with a safe fallback. */
export function getFeatureName(moduleId: number, featureId: number): string {
  return (
    ROLE_MODULE_CATALOG[moduleId]?.features[featureId]?.name ??
    `Feature ${featureId}`
  )
}

/** Description for a feature id, with an empty fallback. */
export function getFeatureDescription(
  moduleId: number,
  featureId: number
): string {
  return ROLE_MODULE_CATALOG[moduleId]?.features[featureId]?.description ?? ''
}
